<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ChatAttachment } from '@/services/chat/chatHttp.service'
import BModal from '@/components/common/BModal.vue'
import CandidateDocumentContainer from '@/components/recruitment/interviews/CandidateDocumentContainer.vue'
import MaterialSymbolsAttachFileRounded from '~icons/material-symbols/attach-file-rounded'
import MaterialSymbolsDownloadRounded from '~icons/material-symbols/download-rounded'
import MaterialSymbolsPictureAsPdfRounded from '~icons/material-symbols/picture-as-pdf-rounded'
import MaterialSymbolsCloseRounded from '~icons/material-symbols/close-rounded'
import MaterialSymbolsOpenInFullRounded from '~icons/material-symbols/open-in-full-rounded'
import { displayLocale } from '@/services/application/displayLocale'

const { t } = useI18n()

const props = defineProps<{
  attachment: ChatAttachment
  tone?: 'default' | 'on-primary'
}>()

const showPdfPreview = ref(false)
const showImageFullscreen = ref(false)
const pdfScale = ref(1.25)

const onPrimary = computed(() => props.tone === 'on-primary')
const isImage = computed(() => String(props.attachment.fileType || '').startsWith('image/'))
const isPdf = computed(() => String(props.attachment.fileType || '').includes('pdf'))
const isExpired = computed(() => {
  if (props.attachment.isExpired) return true
  const expires = props.attachment.expiresAt instanceof Date
    ? props.attachment.expiresAt
    : new Date(props.attachment.expiresAt)
  return expires.getTime() <= Date.now()
})

const displayName = computed(() =>
  props.attachment.fileDescription || props.attachment.originalFileName || t('modules.chat.file.untitled'),
)

const sizeLabel = computed(() => {
  const bytes = props.attachment.fileSize || 0
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
})

const expiresLabel = computed(() => {
  const expires = props.attachment.expiresAt instanceof Date
    ? props.attachment.expiresAt
    : new Date(props.attachment.expiresAt)
  return new Intl.DateTimeFormat(displayLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(expires)
})

const openDownload = () => {
  if (!props.attachment.filePath || isExpired.value) return
  window.open(props.attachment.filePath, '_blank', 'noopener,noreferrer')
}
</script>

<template>
  <div
    class="mt-1.5 rounded-xl border overflow-hidden"
    data-cy="chat-attachment"
    :data-attachment-name="displayName"
    :class="onPrimary
      ? 'border-white/25 bg-white/10'
      : 'border-black/10 dark:border-white/15 bg-black/3 dark:bg-white/5'"
  >
    <img
      v-if="isImage && attachment.filePath && !isExpired"
      :src="attachment.filePath"
      :alt="displayName"
      class="w-full max-h-56 object-contain bg-black/5 cursor-zoom-in"
      loading="lazy"
      @click="showImageFullscreen = true"
    >

    <div class="px-2.5 py-2 space-y-1.5">
      <div class="flex items-start gap-2">
        <div
          class="mt-0.5 shrink-0"
          :class="onPrimary ? 'text-white/90' : 'text-primary-500'"
        >
          <MaterialSymbolsPictureAsPdfRounded
            v-if="isPdf"
            class="w-5 h-5"
          />
          <MaterialSymbolsAttachFileRounded
            v-else
            class="w-5 h-5"
          />
        </div>
        <div class="min-w-0 flex-1">
          <div
            class="text-sm font-medium truncate"
            data-cy="chat-attachment-name"
          >
            {{ displayName }}
          </div>
          <div
            class="text-[11px] opacity-70"
          >
            {{ sizeLabel }}
          </div>
        </div>
        <div class="flex items-center gap-0.5 shrink-0">
          <button
            v-if="isImage && attachment.filePath && !isExpired"
            type="button"
            class="p-1 rounded-sm bg-hover"
            :title="t('modules.chat.file.fullscreen')"
            @click="showImageFullscreen = true"
          >
            <MaterialSymbolsOpenInFullRounded class="w-4 h-4" />
          </button>
          <button
            v-if="isPdf && attachment.filePath && !isExpired"
            type="button"
            class="px-1.5 py-1 rounded-sm bg-hover text-[11px] font-medium"
            @click="showPdfPreview = true"
          >
            {{ t('modules.chat.file.previewPdf') }}
          </button>
          <button
            v-if="attachment.filePath && !isExpired"
            type="button"
            class="p-1 rounded-sm bg-hover"
            :title="t('modules.chat.file.download')"
            @click="openDownload"
          >
            <MaterialSymbolsDownloadRounded class="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        class="rounded-md px-2 py-1 text-[11px] leading-snug"
        :class="isExpired
          ? (onPrimary ? 'bg-white/15 text-white' : 'bg-error/10 text-error')
          : (onPrimary ? 'bg-white/15 text-white' : 'bg-amber-500/15 text-amber-800 dark:text-amber-200')"
      >
        <template v-if="isExpired">
          {{ t('modules.chat.file.expiredNotice', { date: expiresLabel }) }}
        </template>
        <template v-else>
          {{ t('modules.chat.file.expiresNotice', { date: expiresLabel }) }}
        </template>
      </div>
    </div>
  </div>

  <BModal
    v-model="showPdfPreview"
    position="right"
    class="w-full md:w-[80vw] xl:w-[1100px] 2xl:w-[1300px]"
  >
    <div class="flex items-center justify-between gap-2 mb-2">
      <div class="font-medium text-sm truncate">
        {{ displayName }}
      </div>
      <button
        type="button"
        class="p-1 rounded-sm bg-hover"
        @click="showPdfPreview = false"
      >
        <MaterialSymbolsCloseRounded class="w-5 h-5" />
      </button>
    </div>
    <CandidateDocumentContainer
      v-if="showPdfPreview && attachment.filePath"
      :file-path="attachment.filePath"
      :file-name="displayName"
      :scale="pdfScale"
    />
  </BModal>

  <Teleport to="body">
    <div
      v-if="showImageFullscreen && attachment.filePath"
      class="fixed inset-0 z-80 bg-black/90 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      @click.self="showImageFullscreen = false"
    >
      <button
        type="button"
        class="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
        :title="t('common.close')"
        @click="showImageFullscreen = false"
      >
        <MaterialSymbolsCloseRounded class="w-6 h-6" />
      </button>
      <img
        :src="attachment.filePath"
        :alt="displayName"
        class="max-w-full max-h-full object-contain"
      >
    </div>
  </Teleport>
</template>
